import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { Button } from '../components/shared/Button';
import { Eye, EyeOff, KeyRound, CheckCircle, AlertCircle, LogIn } from 'lucide-react';

interface FormData {
  password: string;
  passwordConfirm: string;
}

interface FormErrors {
  password?: string;
  passwordConfirm?: string;
}

export const PasswordResetConfirmPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [formData, setFormData] = useState<FormData>({
    password: '',
    passwordConfirm: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Password validation: 8–32 chars, upper, lower, digit, special character
    const pwd = formData.password;
    if (!pwd) {
      newErrors.password = t('validation.password');
    } else if (pwd.length < 8) {
      newErrors.password = t('validation.passwordMin');
    } else if (pwd.length > 32) {
      newErrors.password = t('validation.passwordMax');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\x20-\x7E]+$/.test(pwd)) {
      newErrors.password = t('validation.passwordComplexity');
    }

    // Password confirmation
    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = t('validation.passwordConfirm');
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = t('validation.passwordMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const { authService } = await import('../services/auth.service');
      await authService.resetPassword(token, formData.password);
      setIsSuccess(true);
    } catch (error: any) {
      setApiError(error?.response?.data?.message || t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const getPasswordStrength = (): { label: string; color: string; width: string } => {
    const { password } = formData;
    if (!password) return { label: '', color: '', width: '0%' };
    if (password.length < 6) return { label: t('auth.passwordStrength.weak'), color: 'bg-error', width: '25%' };
    if (password.length < 8) return { label: t('auth.passwordStrength.fair'), color: 'bg-warning', width: '50%' };
    if (password.length < 12) return { label: t('auth.passwordStrength.good'), color: 'bg-success', width: '75%' };
    return { label: t('auth.passwordStrength.strong'), color: 'bg-success', width: '100%' };
  };

  const passwordStrength = getPasswordStrength();

  // Success state
  if (isSuccess) {
    return (
      <AuthLayout
        variant="reset"
        title={t('auth.resetPassword.confirm.success.title')}
        subtitle={t('auth.resetPassword.confirm.success.subtitle')}
      >
        <AuthCard
          title={t('auth.resetPassword.confirm.success.title')}
          subtitle={t('auth.resetPassword.confirm.success.subtitle')}
        >
          <div className="text-center space-y-6">
            {/* Success Icon */}
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <CheckCircle className="h-8 w-8 text-success" />
            </div>

            {/* Message */}
            <p className="text-gray-700">
              {t('auth.resetPassword.confirm.success.message')}
            </p>

            {/* Go to Login */}
            <Link to="/login">
              <Button
                variant="primary"
                size="large"
                className="w-full"
                icon={<LogIn className="w-5 h-5" />}
              >
                {t('auth.resetPassword.confirm.success.cta')}
              </Button>
            </Link>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // Reset form state
  return (
    <AuthLayout
      variant="reset"
      title={t('auth.resetPassword.confirm.title')}
      subtitle={t('auth.resetPassword.confirm.subtitle')}
    >
      <AuthCard
        label={t('auth.resetPassword.confirm.cardLabel')}
        labelColor="primary"
        title={t('auth.resetPassword.confirm.cardTitle')}
        subtitle={t('auth.resetPassword.confirm.cardSubtitle')}
        footer={{
          text: t('auth.resetPassword.confirm.rememberPassword'),
          linkText: t('auth.resetPassword.confirm.backToLogin'),
          href: '/login',
        }}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {apiError && (
            <div className="flex items-center gap-2 rounded-2xl border border-error/25 bg-error/10 px-4 py-3 text-sm text-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {apiError}
            </div>
          )}

          {!token && (
            <div className="rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
              {t('auth.resetPassword.confirm.noToken')}
            </div>
          )}

          {/* New Password */}
          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.resetPassword.confirm.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={t('auth.resetPassword.confirm.passwordPlaceholder')}
                autoComplete="new-password"
                className={`w-full rounded-2xl border px-4 py-3 pr-12 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.password ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
                }`}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : 'password-hint'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {/* Password strength indicator */}
            {formData.password && (
              <div className="mt-2">
                <div className="h-1 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={`h-full ${passwordStrength.color} transition-all duration-300`}
                    style={{ width: passwordStrength.width }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t('auth.passwordStrength.label')}: <span className={passwordStrength.color.replace('bg-', 'text-')}>{passwordStrength.label}</span>
                </p>
              </div>
            )}
            {errors.password && (
              <p id="password-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.password}
              </p>
            )}
            {!errors.password && !formData.password && (
              <p id="password-hint" className="mt-1 text-xs text-slate-500">
                {t('auth.resetPassword.confirm.passwordPlaceholder')}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="passwordConfirm" className="mb-1 block text-sm font-medium text-slate-700">
              {t('auth.resetPassword.confirm.passwordConfirm')}
            </label>
            <div className="relative">
              <input
                id="passwordConfirm"
                type={showPasswordConfirm ? 'text' : 'password'}
                value={formData.passwordConfirm}
                onChange={(e) => handleChange('passwordConfirm', e.target.value)}
                placeholder={t('auth.resetPassword.confirm.passwordConfirmPlaceholder')}
                autoComplete="new-password"
                className={`w-full rounded-2xl border px-4 py-3 pr-12 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  errors.passwordConfirm ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
                }`}
                aria-invalid={!!errors.passwordConfirm}
                aria-describedby={errors.passwordConfirm ? 'passwordConfirm-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                aria-label={showPasswordConfirm ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.passwordConfirm && (
              <p id="passwordConfirm-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.passwordConfirm}
              </p>
            )}
            {/* Match indicator */}
            {formData.passwordConfirm && formData.password === formData.passwordConfirm && (
              <p className="text-sm text-success mt-1 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                {t('auth.resetPassword.confirm.passwordsMatch')}
              </p>
            )}
          </div>

          {/* Tips */}
          <div className="space-y-1 rounded-2xl border border-primary/10 bg-primary/5 p-3 text-sm text-slate-600">
            <p className="font-medium text-slate-700">{t('auth.resetPassword.confirm.tips.title')}</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>{t('auth.resetPassword.confirm.tips.minLength')}</li>
              <li>{t('auth.resetPassword.confirm.tips.combination')}</li>
              <li>{t('auth.resetPassword.confirm.tips.noPersonal')}</li>
              <li>{t('auth.resetPassword.confirm.tips.noReuse')}</li>
            </ul>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon={<KeyRound className="w-5 h-5" />}
          >
            {isSubmitting ? `${t('auth.resetPassword.confirm.cta')}...` : t('auth.resetPassword.confirm.cta')}
          </Button>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default PasswordResetConfirmPage;
